import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gz_admin_token";

const CATEGORY_LABELS = {
  day0: "Day 0 — Chapter Delivery",
  day2: "Day 2 — Reply Driver",
  day5: "Day 5 — Booking CTA",
  booking_notif: "Booking Alert → Cherisse",
  lead_notif: "New Lead Alert → Cherisse",
};

export default function AdminDashboard() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [inputToken, setInputToken] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchStats = async (t) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setStats(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        setError("Invalid token");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        setError(e.message || "Failed to load stats");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchStats(token);
  }, [token]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!inputToken.trim()) return;
    localStorage.setItem(TOKEN_KEY, inputToken.trim());
    setToken(inputToken.trim());
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setStats(null);
  };

  // ---------- Login screen ----------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-8">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-md bg-[#0a0a0a] border border-[#262626] p-10"
          data-testid="admin-login-form"
        >
          <div className="border-t-4 border-[#EC1985] pt-6 mb-8">
            <div className="text-[#EC1985] text-xs tracking-[3px] uppercase font-bold mb-2">
              Gary Zelesky · Admin
            </div>
            <h1 className="text-3xl font-bold">Dashboard Access</h1>
          </div>
          <label className="block text-xs uppercase tracking-widest text-neutral-400 mb-3">
            Admin Token
          </label>
          <input
            type="password"
            value={inputToken}
            onChange={(e) => setInputToken(e.target.value)}
            placeholder="Paste ADMIN_TOKEN"
            className="w-full bg-black border border-[#262626] text-white px-4 py-3 focus:border-[#EC1985] focus:outline-none"
            data-testid="admin-token-input"
            autoFocus
          />
          {error && (
            <div className="text-red-400 text-sm mt-3" data-testid="admin-login-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            className="mt-6 w-full bg-[#EC1985] text-white px-6 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#d1176f] transition-colors"
            data-testid="admin-login-submit"
          >
            Unlock Dashboard
          </button>
        </form>
      </div>
    );
  }

  // ---------- Loading ----------
  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-[#EC1985] uppercase tracking-widest text-sm">Loading dashboard…</div>
      </div>
    );
  }

  if (!stats) return null;

  // ---------- Dashboard ----------
  return (
    <div className="min-h-screen bg-[#050505] text-white p-6 md:p-12" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-12 border-t-4 border-[#EC1985] pt-6">
        <div>
          <div className="text-[#EC1985] text-xs tracking-[3px] uppercase font-bold mb-1">
            Gary Zelesky · Command Center
          </div>
          <h1 className="text-4xl md:text-5xl font-bold">Dashboard</h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => fetchStats(token)}
            className="px-4 py-2 border border-[#262626] text-neutral-300 uppercase tracking-widest text-xs hover:border-[#EC1985] hover:text-white"
            data-testid="admin-refresh"
          >
            Refresh
          </button>
          <button
            onClick={handleLogout}
            className="px-4 py-2 border border-[#262626] text-neutral-300 uppercase tracking-widest text-xs hover:border-red-500 hover:text-red-400"
            data-testid="admin-logout"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Top-line KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
        <KpiCard label="Total Leads" value={stats.totals.leads} testid="kpi-leads" />
        <KpiCard label="Bookings" value={stats.totals.bookings} testid="kpi-bookings" />
        <KpiCard label="Newsletter" value={stats.totals.newsletter} testid="kpi-newsletter" />
        <KpiCard label="Lead → Booking" value={stats.totals.lead_to_booking_conversions} testid="kpi-conv" />
        <KpiCard label="Conv. Rate" value={`${stats.totals.conversion_rate}%`} testid="kpi-conv-rate" accent />
      </div>

      {/* Email performance */}
      <SectionTitle>Email Performance</SectionTitle>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-12">
        {Object.entries(stats.emails).map(([cat, s]) => (
          <EmailCard key={cat} label={CATEGORY_LABELS[cat] || cat} stats={s} />
        ))}
      </div>

      {/* Nurture queue */}
      <SectionTitle>Nurture Queue</SectionTitle>
      <div className="grid grid-cols-3 gap-4 mb-12">
        <QueueCard label="Pending" value={stats.queue.pending} color="text-yellow-400" />
        <QueueCard label="Sent" value={stats.queue.sent} color="text-green-400" />
        <QueueCard label="Failed" value={stats.queue.failed} color="text-red-400" />
      </div>

      {/* Recent bookings */}
      <SectionTitle>Recent Bookings</SectionTitle>
      <RecentTable
        rows={stats.recent_bookings}
        columns={["name", "email", "organization", "event_type", "created_at"]}
        empty="No bookings yet."
        testid="recent-bookings-table"
      />

      {/* Recent leads */}
      <div className="mt-12">
        <SectionTitle>Recent Lead-Magnet Downloads</SectionTitle>
        <RecentTable
          rows={stats.recent_leads}
          columns={["name", "email", "created_at"]}
          empty="No leads yet."
          testid="recent-leads-table"
        />
      </div>

      <div className="mt-16 text-neutral-500 text-xs uppercase tracking-widest text-center">
        Data refreshes on demand · Emails tracked via Resend webhooks
      </div>
    </div>
  );
}

// ---------- Sub-components ----------
const SectionTitle = ({ children }) => (
  <div className="text-[#EC1985] text-xs uppercase tracking-[3px] font-bold mb-4">
    {children}
  </div>
);

const KpiCard = ({ label, value, accent, testid }) => (
  <div
    className={`p-6 border ${accent ? "border-[#EC1985] bg-[#EC1985]/5" : "border-[#262626] bg-[#0a0a0a]"}`}
    data-testid={testid}
  >
    <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2">{label}</div>
    <div className={`text-3xl font-bold ${accent ? "text-[#EC1985]" : "text-white"}`}>{value}</div>
  </div>
);

const EmailCard = ({ label, stats }) => (
  <div className="p-6 border border-[#262626] bg-[#0a0a0a]">
    <div className="text-xs uppercase tracking-widest text-neutral-300 font-bold mb-4">
      {label}
    </div>
    <div className="grid grid-cols-4 gap-3 text-center">
      <div>
        <div className="text-2xl font-bold text-white">{stats.sent}</div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Sent</div>
      </div>
      <div>
        <div className="text-2xl font-bold text-[#EC1985]">{stats.open_rate}%</div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
          Open {stats.opened}
        </div>
      </div>
      <div>
        <div className="text-2xl font-bold text-green-400">{stats.click_rate}%</div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
          Click {stats.clicked}
        </div>
      </div>
      <div>
        <div className="text-2xl font-bold text-red-400">{stats.bounced}</div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Bounced</div>
      </div>
    </div>
  </div>
);

const QueueCard = ({ label, value, color }) => (
  <div className="p-6 border border-[#262626] bg-[#0a0a0a]">
    <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2">{label}</div>
    <div className={`text-3xl font-bold ${color}`}>{value}</div>
  </div>
);

const RecentTable = ({ rows, columns, empty, testid }) => {
  if (!rows || rows.length === 0) {
    return (
      <div className="p-8 border border-[#262626] bg-[#0a0a0a] text-center text-neutral-500 text-sm">
        {empty}
      </div>
    );
  }
  const fmt = (v) => {
    if (!v) return "—";
    if (typeof v === "string" && v.includes("T") && v.includes(":")) {
      try {
        return new Date(v).toLocaleString();
      } catch {
        return v;
      }
    }
    return v;
  };
  return (
    <div className="border border-[#262626] bg-[#0a0a0a] overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#262626]">
            {columns.map((c) => (
              <th
                key={c}
                className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-neutral-400 font-bold"
              >
                {c.replace(/_/g, " ")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[#1a1a1a] hover:bg-[#121212]">
              {columns.map((c) => (
                <td key={c} className="px-4 py-3 text-neutral-200">
                  {fmt(r[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
